/**
 * Cavalry Charge Effect - Visual effects for cavalry lance charge attacks
 * Features:
 * - Lance trail effect following the charge path
 * - Impact flash and sparks on hit
 * - Ground dust explosion from horse hooves
 * - Shockwave effect for high-damage impacts
 * - Debris particles flying on impact
 */

import * as THREE from 'three';
import { EventBus } from '@/core/events/EventBus';

interface LanceTrail {
  startX: number;
  startY: number;
  startZ: number;
  endX: number;
  endY: number;
  endZ: number;
  rotation: number;
  age: number;
  maxAge: number;
}

interface ImpactFlash {
  x: number;
  y: number;
  z: number;
  age: number;
  maxAge: number;
  scale: number;
  damage: number;
}

interface SparkParticle {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  age: number;
  maxAge: number;
  size: number;
}

interface DustCloud {
  x: number;
  y: number;
  z: number;
  age: number;
  maxAge: number;
  scale: number;
  rotation: number;
}

interface Shockwave {
  x: number;
  y: number;
  z: number;
  age: number;
  maxAge: number;
  rotation: number;
}

interface DebrisParticle {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  age: number;
  maxAge: number;
  size: number;
  rotationSpeed: number;
}

const MAX_LANCE_TRAILS = 60;
const MAX_IMPACT_FLASHES = 40;
const MAX_SPARKS = 600;
const MAX_DUST_CLOUDS = 80;
const MAX_SHOCKWAVES = 30;
const MAX_DEBRIS = 300;

// Shader for lance trail
const trailVertexShader = `
  attribute float alpha;
  varying float vAlpha;
  varying vec2 vUv;

  void main() {
    vAlpha = alpha;
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const trailFragmentShader = `
  uniform float progress;
  uniform vec3 color;
  uniform float time;
  varying float vAlpha;
  varying vec2 vUv;

  void main() {
    // Trail gradient - brighter at the tip
    float tipBrightness = pow(1.0 - vUv.y, 0.8);

    // Fade based on progress
    float fade = 1.0 - progress;
    fade = pow(fade, 0.6);

    // Steel blue shimmer
    float shimmer = sin(vUv.y * 30.0 - time * 25.0) * 0.15 + 0.85;

    // Core color with white tip
    vec3 coreColor = mix(color, vec3(1.0, 1.0, 1.0), tipBrightness * 0.7);

    float alpha = tipBrightness * fade * shimmer * vAlpha;

    gl_FragColor = vec4(coreColor, alpha);
  }
`;

// Shader for impact flash
const flashVertexShader = `
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const flashFragmentShader = `
  uniform float progress;
  uniform vec3 color;
  uniform float intensity;
  varying vec2 vUv;

  void main() {
    vec2 center = vUv - vec2(0.5);
    float dist = length(center) * 2.0;

    // Expanding ring
    float ring = 1.0 - smoothstep(progress * 0.6, progress, dist);

    // Inner flash
    float flash = 1.0 - smoothstep(0.0, progress * 0.4, dist);

    // Fade out
    float fade = 1.0 - progress;

    // Combine
    float alpha = (ring * 0.6 + flash * 0.8) * fade * intensity;

    // White hot core fading to color
    vec3 finalColor = mix(color, vec3(1.0), flash * 0.7);

    gl_FragColor = vec4(finalColor, alpha);
  }
`;

// Shader for sparks
const sparkVertexShader = `
  attribute float size;
  attribute float alpha;
  varying float vAlpha;

  void main() {
    vAlpha = alpha;
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = size * (300.0 / -mvPosition.z);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const sparkFragmentShader = `
  uniform vec3 color;
  varying float vAlpha;

  void main() {
    vec2 center = gl_PointCoord - vec2(0.5);
    float dist = length(center);
    if (dist > 0.5) discard;

    float alpha = (1.0 - dist * 2.0) * vAlpha;

    // Hot yellow-white core
    vec3 finalColor = mix(color, vec3(1.0, 1.0, 0.8), 1.0 - dist * 1.5);

    gl_FragColor = vec4(finalColor, alpha);
  }
`;

// Shader for dust clouds
const dustVertexShader = `
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const dustFragmentShader = `
  uniform float progress;
  uniform vec3 color;
  varying vec2 vUv;

  void main() {
    vec2 center = vUv - vec2(0.5);
    float dist = length(center) * 2.0;

    // Dust expands outward in a ring
    float innerEdge = progress * 0.5;
    float outerEdge = progress;
    float ring = smoothstep(innerEdge, innerEdge + 0.2, dist) * (1.0 - smoothstep(outerEdge - 0.1, outerEdge, dist));

    // Fade out
    float fade = 1.0 - pow(progress, 0.7);

    // Noise-like variation
    float noise = fract(sin(dot(vUv, vec2(12.9898, 78.233))) * 43758.5453);

    float alpha = ring * fade * 0.5 * (0.7 + noise * 0.3);

    gl_FragColor = vec4(color, alpha);
  }
`;

// Shader for shockwave
const shockwaveVertexShader = `
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const shockwaveFragmentShader = `
  uniform float progress;
  uniform float time;
  uniform vec3 color;
  varying vec2 vUv;

  void main() {
    vec2 center = vUv - vec2(0.5);
    float dist = length(center) * 2.0;

    // Shockwave ring expanding
    float ringPos = progress;
    float ringWidth = 0.12 * (1.0 - progress * 0.5);

    float ring = 1.0 - smoothstep(ringPos - ringWidth, ringPos, dist);
    ring *= smoothstep(ringPos, ringPos + ringWidth * 0.5, dist);

    // Fade out
    float fade = 1.0 - progress;

    // Directional bias (forward-facing wave)
    float dirBias = 1.0 - abs(atan(center.y, center.x)) / 3.14159;
    dirBias = 0.5 + dirBias * 0.5;

    float alpha = ring * fade * dirBias * 0.7;

    // Slight color variation
    vec3 finalColor = mix(color, vec3(1.0), ring * 0.3);

    gl_FragColor = vec4(finalColor, alpha);
  }
`;

// Debris shader
const debrisVertexShader = `
  attribute float size;
  attribute float alpha;
  varying float vAlpha;

  void main() {
    vAlpha = alpha;
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = size * (200.0 / -mvPosition.z);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const debrisFragmentShader = `
  uniform vec3 color;
  varying float vAlpha;

  void main() {
    vec2 center = gl_PointCoord - vec2(0.5);
    float dist = length(center);
    if (dist > 0.5) discard;

    // Square-ish debris shape
    float shape = max(abs(center.x), abs(center.y));
    if (shape > 0.4) discard;

    float alpha = vAlpha * (1.0 - shape * 2.0);

    gl_FragColor = vec4(color, alpha);
  }
`;

export class CavalryChargeEffect {
  private scene: THREE.Scene;
  private time = 0;

  // Lance trails
  private lanceTrails: LanceTrail[] = [];
  private trailGeometry: THREE.BufferGeometry;
  private trailMaterials: THREE.ShaderMaterial[] = [];
  private trailMeshes: THREE.Mesh[] = [];

  // Impact flashes
  private impactFlashes: ImpactFlash[] = [];
  private flashGeometry: THREE.PlaneGeometry;
  private flashMaterials: THREE.ShaderMaterial[] = [];
  private flashMeshes: THREE.Mesh[] = [];

  // Spark particles
  private sparks: SparkParticle[] = [];
  private sparkGeometry: THREE.BufferGeometry;
  private sparkMaterial: THREE.ShaderMaterial;
  private sparkPoints: THREE.Points;
  private sparkPositions: Float32Array;
  private sparkSizes: Float32Array;
  private sparkAlphas: Float32Array;

  // Dust clouds
  private dustClouds: DustCloud[] = [];
  private dustGeometry: THREE.PlaneGeometry;
  private dustMaterials: THREE.ShaderMaterial[] = [];
  private dustMeshes: THREE.Mesh[] = [];

  // Shockwaves
  private shockwaves: Shockwave[] = [];
  private shockwaveGeometry: THREE.PlaneGeometry;
  private shockwaveMaterials: THREE.ShaderMaterial[] = [];
  private shockwaveMeshes: THREE.Mesh[] = [];

  // Debris particles
  private debris: DebrisParticle[] = [];
  private debrisGeometry: THREE.BufferGeometry;
  private debrisMaterial: THREE.ShaderMaterial;
  private debrisPoints: THREE.Points;
  private debrisPositions: Float32Array;
  private debrisSizes: Float32Array;
  private debrisAlphas: Float32Array;

  // Colors
  private trailColor = new THREE.Color(0x6699cc);     // Steel blue
  private flashColor = new THREE.Color(0xffdd88);     // Golden flash
  private sparkColor = new THREE.Color(0xffaa44);     // Orange-yellow sparks
  private dustColor = new THREE.Color(0x8b7355);      // Brown dust
  private shockwaveColor = new THREE.Color(0x88aacc); // Light blue shockwave
  private debrisColor = new THREE.Color(0x665544);    // Dark brown debris

  constructor(scene: THREE.Scene) {
    this.scene = scene;

    // Initialize lance trail geometry
    this.trailGeometry = this.createTrailGeometry();
    this.initTrailMeshes();

    // Initialize impact flash
    this.flashGeometry = new THREE.PlaneGeometry(1, 1);
    this.initFlashMeshes();

    // Initialize sparks
    this.sparkPositions = new Float32Array(MAX_SPARKS * 3);
    this.sparkSizes = new Float32Array(MAX_SPARKS);
    this.sparkAlphas = new Float32Array(MAX_SPARKS);
    this.sparkGeometry = new THREE.BufferGeometry();
    this.sparkGeometry.setAttribute('position', new THREE.BufferAttribute(this.sparkPositions, 3));
    this.sparkGeometry.setAttribute('size', new THREE.BufferAttribute(this.sparkSizes, 1));
    this.sparkGeometry.setAttribute('alpha', new THREE.BufferAttribute(this.sparkAlphas, 1));

    this.sparkMaterial = new THREE.ShaderMaterial({
      vertexShader: sparkVertexShader,
      fragmentShader: sparkFragmentShader,
      uniforms: {
        color: { value: this.sparkColor },
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    this.sparkPoints = new THREE.Points(this.sparkGeometry, this.sparkMaterial);
    this.sparkPoints.frustumCulled = false;
    this.scene.add(this.sparkPoints);

    // Initialize dust clouds
    this.dustGeometry = new THREE.PlaneGeometry(1, 1);
    this.initDustMeshes();

    // Initialize shockwaves
    this.shockwaveGeometry = new THREE.PlaneGeometry(3, 3);
    this.initShockwaveMeshes();

    // Initialize debris
    this.debrisPositions = new Float32Array(MAX_DEBRIS * 3);
    this.debrisSizes = new Float32Array(MAX_DEBRIS);
    this.debrisAlphas = new Float32Array(MAX_DEBRIS);
    this.debrisGeometry = new THREE.BufferGeometry();
    this.debrisGeometry.setAttribute('position', new THREE.BufferAttribute(this.debrisPositions, 3));
    this.debrisGeometry.setAttribute('size', new THREE.BufferAttribute(this.debrisSizes, 1));
    this.debrisGeometry.setAttribute('alpha', new THREE.BufferAttribute(this.debrisAlphas, 1));

    this.debrisMaterial = new THREE.ShaderMaterial({
      vertexShader: debrisVertexShader,
      fragmentShader: debrisFragmentShader,
      uniforms: {
        color: { value: this.debrisColor },
      },
      transparent: true,
      blending: THREE.NormalBlending,
      depthWrite: false,
    });

    this.debrisPoints = new THREE.Points(this.debrisGeometry, this.debrisMaterial);
    this.debrisPoints.frustumCulled = false;
    this.scene.add(this.debrisPoints);

    // Setup event listeners
    this.setupEventListeners();
  }

  private createTrailGeometry(): THREE.BufferGeometry {
    // Create a tapered trail shape
    const segments = 12;
    const vertices: number[] = [];
    const uvs: number[] = [];
    const indices: number[] = [];
    const alphas: number[] = [];

    const trailLength = 1.5;
    const trailWidth = 0.15;

    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const width = trailWidth * (1 - t * 0.7); // Taper toward end

      // Left vertex
      vertices.push(-width / 2, 0, -t * trailLength);
      uvs.push(0, t);
      alphas.push(1 - t * 0.5);

      // Right vertex
      vertices.push(width / 2, 0, -t * trailLength);
      uvs.push(1, t);
      alphas.push(1 - t * 0.5);
    }

    // Create triangles
    for (let i = 0; i < segments; i++) {
      const base = i * 2;
      indices.push(base, base + 1, base + 2);
      indices.push(base + 1, base + 3, base + 2);
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geometry.setAttribute('alpha', new THREE.Float32BufferAttribute(alphas, 1));
    geometry.setIndex(indices);

    return geometry;
  }

  private initTrailMeshes(): void {
    for (let i = 0; i < MAX_LANCE_TRAILS; i++) {
      const material = new THREE.ShaderMaterial({
        vertexShader: trailVertexShader,
        fragmentShader: trailFragmentShader,
        uniforms: {
          progress: { value: 0 },
          color: { value: this.trailColor },
          time: { value: 0 },
        },
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      this.trailMaterials.push(material);

      const mesh = new THREE.Mesh(this.trailGeometry, material);
      mesh.visible = false;
      this.scene.add(mesh);
      this.trailMeshes.push(mesh);
    }
  }

  private initFlashMeshes(): void {
    for (let i = 0; i < MAX_IMPACT_FLASHES; i++) {
      const material = new THREE.ShaderMaterial({
        vertexShader: flashVertexShader,
        fragmentShader: flashFragmentShader,
        uniforms: {
          progress: { value: 0 },
          color: { value: this.flashColor },
          intensity: { value: 1.0 },
        },
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      this.flashMaterials.push(material);

      const mesh = new THREE.Mesh(this.flashGeometry, material);
      mesh.visible = false;
      this.scene.add(mesh);
      this.flashMeshes.push(mesh);
    }
  }

  private initDustMeshes(): void {
    for (let i = 0; i < MAX_DUST_CLOUDS; i++) {
      const material = new THREE.ShaderMaterial({
        vertexShader: dustVertexShader,
        fragmentShader: dustFragmentShader,
        uniforms: {
          progress: { value: 0 },
          color: { value: this.dustColor },
        },
        transparent: true,
        blending: THREE.NormalBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      this.dustMaterials.push(material);

      const mesh = new THREE.Mesh(this.dustGeometry, material);
      mesh.rotation.x = -Math.PI / 2;
      mesh.visible = false;
      this.scene.add(mesh);
      this.dustMeshes.push(mesh);
    }
  }

  private initShockwaveMeshes(): void {
    for (let i = 0; i < MAX_SHOCKWAVES; i++) {
      const material = new THREE.ShaderMaterial({
        vertexShader: shockwaveVertexShader,
        fragmentShader: shockwaveFragmentShader,
        uniforms: {
          progress: { value: 0 },
          time: { value: 0 },
          color: { value: this.shockwaveColor },
        },
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      this.shockwaveMaterials.push(material);

      const mesh = new THREE.Mesh(this.shockwaveGeometry, material);
      mesh.rotation.x = -Math.PI / 2;
      mesh.visible = false;
      this.scene.add(mesh);
      this.shockwaveMeshes.push(mesh);
    }
  }

  private setupEventListeners(): void {
    EventBus.on('cavalry:charge', (data: {
      x: number;
      y: number;
      z: number;
      rotation: number;
      targetX: number;
      targetY: number;
      targetZ: number;
      damage: number;
      speed: number;
    }) => {
      // Spawn lance trail from attacker toward target
      this.spawnLanceTrail(
        data.x, data.y + 0.6, data.z,
        data.targetX, data.targetY + 0.4, data.targetZ,
        data.rotation
      );

      // Spawn impact effects at target
      const impactX = (data.x + data.targetX) / 2;
      const impactY = (data.y + data.targetY) / 2 + 0.4;
      const impactZ = (data.z + data.targetZ) / 2;

      this.spawnImpactFlash(impactX, impactY, impactZ, data.damage);
      this.spawnSparks(impactX, impactY, impactZ, 10 + Math.floor(data.damage / 3));
      this.spawnDebris(impactX, impactY - 0.2, impactZ, 5 + Math.floor(data.damage / 5));

      // Spawn dust clouds at target position and behind horse
      this.spawnDustCloud(data.targetX, data.targetY + 0.05, data.targetZ, data.rotation);
      this.spawnDustCloud(data.x - Math.sin(data.rotation) * 0.5, data.y + 0.05, data.z - Math.cos(data.rotation) * 0.5, data.rotation);

      // Spawn shockwave for high damage attacks
      if (data.damage > 12) {
        this.spawnShockwave(data.targetX, data.targetY + 0.1, data.targetZ, data.rotation);
      }
    });
  }

  private spawnLanceTrail(
    startX: number, startY: number, startZ: number,
    endX: number, endY: number, endZ: number,
    rotation: number
  ): void {
    if (this.lanceTrails.length >= MAX_LANCE_TRAILS) {
      this.lanceTrails.shift();
    }

    this.lanceTrails.push({
      startX, startY, startZ,
      endX, endY, endZ,
      rotation,
      age: 0,
      maxAge: 0.3,
    });
  }

  private spawnImpactFlash(x: number, y: number, z: number, damage: number): void {
    if (this.impactFlashes.length >= MAX_IMPACT_FLASHES) {
      this.impactFlashes.shift();
    }

    this.impactFlashes.push({
      x, y, z,
      age: 0,
      maxAge: 0.25,
      scale: 0.8 + damage * 0.03,
      damage,
    });
  }

  private spawnSparks(x: number, y: number, z: number, count: number): void {
    for (let i = 0; i < count && this.sparks.length < MAX_SPARKS; i++) {
      const angle = Math.random() * Math.PI * 2;
      const upBias = 0.4 + Math.random() * 0.4;
      const speed = 3 + Math.random() * 4;

      this.sparks.push({
        x, y, z,
        vx: Math.cos(angle) * speed * (1 - upBias),
        vy: speed * upBias + Math.random() * 2,
        vz: Math.sin(angle) * speed * (1 - upBias),
        age: 0,
        maxAge: 0.35 + Math.random() * 0.3,
        size: 4 + Math.random() * 5,
      });
    }
  }

  private spawnDustCloud(x: number, y: number, z: number, rotation: number): void {
    if (this.dustClouds.length >= MAX_DUST_CLOUDS) {
      this.dustClouds.shift();
    }

    this.dustClouds.push({
      x, y, z,
      age: 0,
      maxAge: 0.6,
      scale: 1.2 + Math.random() * 0.4,
      rotation,
    });
  }

  private spawnShockwave(x: number, y: number, z: number, rotation: number): void {
    if (this.shockwaves.length >= MAX_SHOCKWAVES) {
      this.shockwaves.shift();
    }

    this.shockwaves.push({
      x, y, z,
      age: 0,
      maxAge: 0.4,
      rotation,
    });
  }

  private spawnDebris(x: number, y: number, z: number, count: number): void {
    for (let i = 0; i < count && this.debris.length < MAX_DEBRIS; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 1.5 + Math.random() * 2.5;

      this.debris.push({
        x, y, z,
        vx: Math.cos(angle) * speed,
        vy: 2 + Math.random() * 3,
        vz: Math.sin(angle) * speed,
        age: 0,
        maxAge: 0.6 + Math.random() * 0.4,
        size: 3 + Math.random() * 4,
        rotationSpeed: (Math.random() - 0.5) * 10,
      });
    }
  }

  update(deltaTime: number): void {
    this.time += deltaTime;

    // Update lance trails
    this.updateLanceTrails(deltaTime);

    // Update impact flashes
    this.updateImpactFlashes(deltaTime);

    // Update sparks
    this.updateSparks(deltaTime);

    // Update dust clouds
    this.updateDustClouds(deltaTime);

    // Update shockwaves
    this.updateShockwaves(deltaTime);

    // Update debris
    this.updateDebris(deltaTime);
  }

  private updateLanceTrails(deltaTime: number): void {
    for (let i = this.lanceTrails.length - 1; i >= 0; i--) {
      const trail = this.lanceTrails[i];
      trail.age += deltaTime;

      if (trail.age >= trail.maxAge) {
        this.lanceTrails.splice(i, 1);
        continue;
      }

      const mesh = this.trailMeshes[i];
      const material = this.trailMaterials[i];
      if (mesh && material) {
        mesh.visible = true;

        // Position at midpoint between start and end
        const midX = (trail.startX + trail.endX) / 2;
        const midY = (trail.startY + trail.endY) / 2;
        const midZ = (trail.startZ + trail.endZ) / 2;
        mesh.position.set(midX, midY, midZ);
        mesh.rotation.y = trail.rotation;

        // Calculate scale based on distance
        const dx = trail.endX - trail.startX;
        const dz = trail.endZ - trail.startZ;
        const dist = Math.sqrt(dx * dx + dz * dz);
        mesh.scale.set(1, 1, dist * 0.8);

        const progress = trail.age / trail.maxAge;
        material.uniforms.progress.value = progress;
        material.uniforms.time.value = this.time;
      }
    }

    // Hide unused trail meshes
    for (let i = this.lanceTrails.length; i < MAX_LANCE_TRAILS; i++) {
      this.trailMeshes[i].visible = false;
    }
  }

  private updateImpactFlashes(deltaTime: number): void {
    for (let i = this.impactFlashes.length - 1; i >= 0; i--) {
      const flash = this.impactFlashes[i];
      flash.age += deltaTime;

      if (flash.age >= flash.maxAge) {
        this.impactFlashes.splice(i, 1);
        continue;
      }

      const mesh = this.flashMeshes[i];
      const material = this.flashMaterials[i];
      if (mesh && material) {
        mesh.visible = true;
        mesh.position.set(flash.x, flash.y, flash.z);

        const progress = flash.age / flash.maxAge;
        const scale = flash.scale * (1 + progress * 1.5);
        mesh.scale.set(scale, scale, 1);

        // Billboard - face camera
        mesh.quaternion.copy(this.scene.children[0]?.quaternion || new THREE.Quaternion());

        material.uniforms.progress.value = progress;
        material.uniforms.intensity.value = Math.min(flash.damage / 15, 1.5);
      }
    }

    // Hide unused flash meshes
    for (let i = this.impactFlashes.length; i < MAX_IMPACT_FLASHES; i++) {
      this.flashMeshes[i].visible = false;
    }
  }

  private updateSparks(deltaTime: number): void {
    const gravity = -18;

    for (let i = this.sparks.length - 1; i >= 0; i--) {
      const spark = this.sparks[i];
      spark.age += deltaTime;

      if (spark.age >= spark.maxAge) {
        this.sparks.splice(i, 1);
        continue;
      }

      // Physics
      spark.vy += gravity * deltaTime;
      spark.x += spark.vx * deltaTime;
      spark.y += spark.vy * deltaTime;
      spark.z += spark.vz * deltaTime;

      // Ground bounce
      if (spark.y < 0.05) {
        spark.y = 0.05;
        spark.vy = -spark.vy * 0.2;
        spark.vx *= 0.4;
        spark.vz *= 0.4;
      }
    }

    // Update geometry
    for (let i = 0; i < MAX_SPARKS; i++) {
      if (i < this.sparks.length) {
        const spark = this.sparks[i];
        const progress = spark.age / spark.maxAge;

        this.sparkPositions[i * 3] = spark.x;
        this.sparkPositions[i * 3 + 1] = spark.y;
        this.sparkPositions[i * 3 + 2] = spark.z;
        this.sparkSizes[i] = spark.size * (1 - progress * 0.4);
        this.sparkAlphas[i] = 1 - progress;
      } else {
        this.sparkAlphas[i] = 0;
      }
    }

    this.sparkGeometry.attributes.position.needsUpdate = true;
    this.sparkGeometry.attributes.size.needsUpdate = true;
    this.sparkGeometry.attributes.alpha.needsUpdate = true;
  }

  private updateDustClouds(deltaTime: number): void {
    for (let i = this.dustClouds.length - 1; i >= 0; i--) {
      const dust = this.dustClouds[i];
      dust.age += deltaTime;

      if (dust.age >= dust.maxAge) {
        this.dustClouds.splice(i, 1);
        continue;
      }

      const mesh = this.dustMeshes[i];
      const material = this.dustMaterials[i];
      if (mesh && material) {
        mesh.visible = true;
        mesh.position.set(dust.x, dust.y, dust.z);

        const progress = dust.age / dust.maxAge;
        const scale = dust.scale * (1 + progress * 2.5);
        mesh.scale.set(scale, scale, 1);

        material.uniforms.progress.value = progress;
      }
    }

    // Hide unused dust meshes
    for (let i = this.dustClouds.length; i < MAX_DUST_CLOUDS; i++) {
      this.dustMeshes[i].visible = false;
    }
  }

  private updateShockwaves(deltaTime: number): void {
    for (let i = this.shockwaves.length - 1; i >= 0; i--) {
      const wave = this.shockwaves[i];
      wave.age += deltaTime;

      if (wave.age >= wave.maxAge) {
        this.shockwaves.splice(i, 1);
        continue;
      }

      const mesh = this.shockwaveMeshes[i];
      const material = this.shockwaveMaterials[i];
      if (mesh && material) {
        mesh.visible = true;
        mesh.position.set(wave.x, wave.y, wave.z);
        mesh.rotation.z = wave.rotation;

        const progress = wave.age / wave.maxAge;
        const scale = 1 + progress * 3;
        mesh.scale.set(scale, scale, 1);

        material.uniforms.progress.value = progress;
        material.uniforms.time.value = this.time;
      }
    }

    // Hide unused shockwave meshes
    for (let i = this.shockwaves.length; i < MAX_SHOCKWAVES; i++) {
      this.shockwaveMeshes[i].visible = false;
    }
  }

  private updateDebris(deltaTime: number): void {
    const gravity = -12;

    for (let i = this.debris.length - 1; i >= 0; i--) {
      const debris = this.debris[i];
      debris.age += deltaTime;

      if (debris.age >= debris.maxAge) {
        this.debris.splice(i, 1);
        continue;
      }

      // Physics
      debris.vy += gravity * deltaTime;
      debris.x += debris.vx * deltaTime;
      debris.y += debris.vy * deltaTime;
      debris.z += debris.vz * deltaTime;

      // Ground stop
      if (debris.y < 0.05) {
        debris.y = 0.05;
        debris.vy = 0;
        debris.vx *= 0.5;
        debris.vz *= 0.5;
      }
    }

    // Update geometry
    for (let i = 0; i < MAX_DEBRIS; i++) {
      if (i < this.debris.length) {
        const debris = this.debris[i];
        const progress = debris.age / debris.maxAge;

        this.debrisPositions[i * 3] = debris.x;
        this.debrisPositions[i * 3 + 1] = debris.y;
        this.debrisPositions[i * 3 + 2] = debris.z;
        this.debrisSizes[i] = debris.size;
        this.debrisAlphas[i] = 1 - progress;
      } else {
        this.debrisAlphas[i] = 0;
      }
    }

    this.debrisGeometry.attributes.position.needsUpdate = true;
    this.debrisGeometry.attributes.size.needsUpdate = true;
    this.debrisGeometry.attributes.alpha.needsUpdate = true;
  }

  dispose(): void {
    // Dispose lance trails
    for (const mesh of this.trailMeshes) {
      this.scene.remove(mesh);
    }
    for (const material of this.trailMaterials) {
      material.dispose();
    }
    this.trailGeometry.dispose();
    this.lanceTrails = [];

    // Dispose impact flashes
    for (const mesh of this.flashMeshes) {
      this.scene.remove(mesh);
    }
    for (const material of this.flashMaterials) {
      material.dispose();
    }
    this.flashGeometry.dispose();
    this.impactFlashes = [];

    // Dispose sparks
    this.scene.remove(this.sparkPoints);
    this.sparkGeometry.dispose();
    this.sparkMaterial.dispose();
    this.sparks = [];

    // Dispose dust clouds
    for (const mesh of this.dustMeshes) {
      this.scene.remove(mesh);
    }
    for (const material of this.dustMaterials) {
      material.dispose();
    }
    this.dustGeometry.dispose();
    this.dustClouds = [];

    // Dispose shockwaves
    for (const mesh of this.shockwaveMeshes) {
      this.scene.remove(mesh);
    }
    for (const material of this.shockwaveMaterials) {
      material.dispose();
    }
    this.shockwaveGeometry.dispose();
    this.shockwaves = [];

    // Dispose debris
    this.scene.remove(this.debrisPoints);
    this.debrisGeometry.dispose();
    this.debrisMaterial.dispose();
    this.debris = [];
  }
}
